import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Ban, Loader2, Mail, Phone } from "lucide-react";
import { api } from "../../../lib/api";

/**
 * CRMRiskTab — surfaces every customer currently on the blacklist plus
 * every crm_profile flagged RESTRICTED / REVIEW_REQUIRED / DO_NOT_RENT.
 * Internal use only.
 */
export default function CRMRiskTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/customers")
      .then((r) => {
        const flagged = (Array.isArray(r.data) ? r.data : []).filter((c) => c.is_blacklisted);
        setRows(flagged);
      })
      .catch(() => toast.error("Failed to load risk list"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="crm-risk-tab" className="space-y-4">
      <div>
        <div className="label-caps text-red-300 text-[10px]">Internal · staff only</div>
        <h3 className="font-serif text-2xl text-white mt-0.5">Risk management</h3>
        <p className="text-white/60 text-xs mt-1 max-w-2xl">
          Customers on the blacklist or with an elevated internal status. Never visible to customers.
        </p>
      </div>

      <div className="rounded-2xl border border-red-400/25 bg-black/25 overflow-hidden">
        <div className="p-3 border-b border-white/5 label-caps text-[10px] text-red-300">
          {loading ? "Loading…" : `${rows.length} flagged customer${rows.length === 1 ? "" : "s"}`}
        </div>
        {loading ? (
          <div className="p-10 text-center text-white/50 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={14} className="animate-spin text-red-300" /> Scanning…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm">No flagged customers. 🎉</div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[70vh] overflow-auto">
            {rows.map((c) => (
              <div key={c.customer_key} className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center shrink-0">
                  <Ban size={14} className="text-red-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-medium">{c.name || "Unknown"}</div>
                  <div className="text-white/50 text-[11px] flex items-center gap-3 flex-wrap mt-0.5">
                    {c.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {c.email}</span>}
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                  </div>
                  {c.blacklist_reason && (
                    <div className="text-red-200 text-[11px] mt-1 italic">"{c.blacklist_reason}"</div>
                  )}
                </div>
                <span className="label-caps text-[9px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-200">BLOCKED</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
